import { LOCALE_TAGS, isLocale } from '@app/shared'
import Decimal from 'decimal.js'

/**
 * Money typesetting. The app's content *is* numbers, so this is not a detail.
 *
 * Two rules drive everything here:
 *
 * 1. Amounts arrive from the API as strings carrying up to 18 decimals. They
 *    never pass through `Number`, which silently loses precision above 2^53 and
 *    on long fractions. Everything below works on strings via decimal.js.
 * 2. Typography is fixed across both locales. A crypto balance is cross-checked
 *    against an exchange, so the grouping and the decimal mark must not shift
 *    when the interface language does — a comma decimal would read as a
 *    thousands separator to half the audience.
 *
 * See DESIGN_SYSTEM.md section 4.
 */

/** U+202F narrow no-break space — the thousands separator. */
const GROUP = ' '
/** U+2212 minus sign. A hyphen is too short and sits at the wrong height. */
const MINUS = '−'

export type Trend = 'rise' | 'fall' | 'flat'

function groupDigits(integerDigits: string): string {
  return integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP)
}

/** The digits of `value` at this many places, rounded towards zero. */
function digitsOf(value: Decimal, decimals: number): string {
  return value.abs().toFixed(decimals, Decimal.ROUND_DOWN)
}

/**
 * Whether a figure typesets as zero at this many decimals. The sign and the
 * colour follow the digits on screen, never a remainder the reader cannot
 * see: a result of −0.004 USDT is "0.00", not a red "−0.00".
 */
export function isDisplayedZero(value: string | number | Decimal, decimals: number): boolean {
  return /^0(\.0*)?$/.test(digitsOf(toDecimal(value), decimals))
}

/** Splits a decimal.js value into its typeset parts, with a real minus sign. */
function compose(value: Decimal, decimals: number, trim: boolean): string {
  const fixed = digitsOf(value, decimals)
  const negative = value.isNegative() && !/^0(\.0*)?$/.test(fixed)
  let [int, frac = ''] = fixed.split('.')

  if (trim) {
    frac = frac.replace(/0+$/, '')
  }

  const body = frac === '' ? groupDigits(int) : `${groupDigits(int)}.${frac}`
  return negative ? `${MINUS}${body}` : body
}

function toDecimal(value: string | number | Decimal): Decimal {
  try {
    return new Decimal(value)
  } catch {
    return new Decimal(0)
  }
}

export interface AmountOptions {
  /** Decimal places to show. Defaults to 8, the practical ceiling for display. */
  decimals?: number
  /** Drop trailing zeros. On by default for crypto, off for fiat. */
  trim?: boolean
  /** Force a leading + on positive values. */
  signed?: boolean
}

/**
 * A crypto amount, trimmed to the asset's real precision rather than padded to
 * eighteen meaningless decimals: `0.01842`, not `0.018420000000000000`.
 */
export function formatAmount(
  value: string | number | Decimal,
  { decimals = 8, trim = true, signed = false }: AmountOptions = {},
): string {
  const d = toDecimal(value)
  const body = compose(d, decimals, trim)
  return signed && d.isPositive() && !isDisplayedZero(d, decimals) ? `+${body}` : body
}

/**
 * A fiat figure. Trailing zeros are kept — `28 754.20`, never `28 754.2` —
 * because a money column with a ragged right edge reads as unfinished.
 */
export function formatMoney(
  value: string | number | Decimal,
  { decimals = 2, signed = false }: Omit<AmountOptions, 'trim'> = {},
): string {
  const d = toDecimal(value)
  const body = compose(d, decimals, false)
  return signed && d.isPositive() && !isDisplayedZero(d, decimals) ? `+${body}` : body
}

/** A price at the market's own precision. */
export function formatPrice(
  value: string | number | Decimal,
  precision = 2,
): string {
  return compose(toDecimal(value), precision, false)
}

/** Percentages are always signed, so a rise and a fall are never ambiguous. */
export function formatPercent(
  value: string | number | Decimal,
  decimals = 2,
): string {
  const d = toDecimal(value)
  const body = compose(d, decimals, false)
  const sign = d.isPositive() && !isDisplayedZero(d, decimals) ? '+' : ''
  return `${sign}${body}%`
}

const COMPACT_STEPS = [
  { limit: '1e12', suffix: 'T' },
  { limit: '1e9', suffix: 'B' },
  { limit: '1e6', suffix: 'M' },
  { limit: '1e3', suffix: 'K' },
] as const

/** Volumes and market caps: `842.5M`. Never used where an exact figure matters. */
export function formatCompact(value: string | number | Decimal): string {
  const d = toDecimal(value)
  const abs = d.abs()

  for (const { limit, suffix } of COMPACT_STEPS) {
    const step = new Decimal(limit)
    if (abs.gte(step)) {
      const scaled = abs.div(step)
      // Three significant figures, the convention every exchange uses:
      // 842.5M, 99.4M, 9.93M — never 842M, which reads as less precise data
      // rather than as a rounder number. The zeros stay for the same reason:
      // 55.0M next to 47.7M is one column set to one precision, 55M is not.
      const body = compose(scaled, scaled.gte(10) ? 1 : 2, false)
      return `${d.isNegative() ? MINUS : ''}${body}${suffix}`
    }
  }
  return compose(d, 2, true)
}

/**
 * The direction a figure is coloured by. With `decimals` the decision is taken
 * on the digits that are shown, so a value that reads "0.00" is flat rather
 * than red; without it the exact value decides, which a ledger row needs to
 * tell a dust credit from a debit.
 */
export function trendOf(value: string | number | Decimal, decimals?: number): Trend {
  const d = toDecimal(value)
  if (d.isZero() || (decimals !== undefined && isDisplayedZero(d, decimals))) return 'flat'
  return d.isPositive() ? 'rise' : 'fall'
}

/**
 * A counted quantity — followers, subscriptions, confirmations.
 *
 * A count is not money, but it is still a numeral on screen, so it is typeset
 * here rather than interpolated raw: 12400 has to read as `12 400`. The object
 * carries the already-typeset text *and* the plain value, because the plural
 * form a locale needs depends on the number, not on the grouped string — and a
 * dictionary must never re-derive one from the other.
 */
export interface Count {
  /** Grouped and ready to interpolate. */
  readonly text: string
  /** The underlying figure, for plural selection only. */
  readonly value: Decimal
}

/** Typesets a count once, at the call site, for a dictionary to interpolate. */
export function counted(value: string | number | Decimal): Count {
  const d = toDecimal(value)
  return { text: compose(d, 0, true), value: d }
}

/** The three Russian plural stems, in the order the language uses them. */
export type RuPluralForms = readonly [one: string, few: string, many: string]

function decimalOf(count: Count | string | number): Decimal {
  return typeof count === 'object' ? count.value : toDecimal(count)
}

/**
 * Russian plural agreement: 1 подписчик / 2 подписчика / 5 подписчиков, with
 * the 11–14 exception that makes a naive last-digit rule wrong. The decision is
 * taken on the digits of the value, never on `Number(...)`.
 */
export function pluralRu(count: Count | string | number, forms: RuPluralForms): string {
  const digits = decimalOf(count).abs().trunc().toFixed(0)
  const [one, few, many] = forms

  // 11–14 take the many form even though they end in 1, 2, 3 and 4.
  if (digits.length >= 2 && digits.slice(-2, -1) === '1') return many

  const last = digits.slice(-1)
  if (last === '1') return one
  if (last === '2' || last === '3' || last === '4') return few
  return many
}

/**
 * French counts 0 and 1 as singular (« 0 abonné », « 1 abonné », « 2 abonnés »);
 * Italian, Spanish and Portuguese use the English rule, and Georgian nouns do
 * not inflect after a numeral at all, so those dictionaries call `pluralEn`.
 */
export function pluralFr(count: Count | string | number, one: string, many: string): string {
  return decimalOf(count).abs().lessThanOrEqualTo(1) ? one : many
}

/**
 * Romanian: 1 takes the singular, 0 and 2–19 the plain plural (« 0 abonați »),
 * and from 20 on the plural is joined with « de » (« 20 de abonați »,
 * « 100 de abonați », but « 101 abonați »). The numeral is already in the
 * string, so the function returns the noun phrase to put after it.
 */
export function pluralRo(count: Count | string | number, one: string, many: string): string {
  const n = decimalOf(count).abs().trunc()
  if (n.equals(1)) return one
  if (n.isZero()) return many
  const hundred = n.mod(100).toNumber()
  return hundred === 0 || hundred > 19 ? `de ${many}` : many
}

/** English agreement: everything but exactly one takes the plural. */
export function pluralEn(count: Count | string | number, one: string, many: string): string {
  return decimalOf(count).abs().eq(1) ? one : many
}

/**
 * A blockchain address, shortened for a row but never for a screen where the
 * user has to verify it — show those in full, wrapped, in mono.
 */
export function shortenAddress(address: string, lead = 6, tail = 4): string {
  if (address.length <= lead + tail + 1) return address
  return `${address.slice(0, lead)}…${address.slice(-tail)}`
}

export function shortenHash(hash: string): string {
  return shortenAddress(hash, 10, 8)
}

const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
}
const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>()

/**
 * The document language is set by the i18n provider, so a helper called from
 * anywhere — a toast, a list row, a sheet — follows the interface language
 * without threading the locale through every call site.
 */
function documentLanguage(): string {
  const lang = typeof document === 'undefined' ? '' : document.documentElement.lang
  return lang === '' ? 'ru' : lang
}

/**
 * The provider sets the bare language on the document (`en`), and Intl reads
 * a bare `en` as en-US: month first, 12-hour clock. The rest of the screen
 * formats with the shared tags (`en-GB`), so a bare code is resolved through
 * the same table before it reaches Intl; a full tag passes through untouched.
 */
function dateLocaleOf(language: string): string {
  return isLocale(language) ? LOCALE_TAGS[language] : language
}

export function formatDateTime(iso: string | Date, language: string = documentLanguage()): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso
  if (Number.isNaN(date.getTime())) return '—'
  const tag = dateLocaleOf(language)
  let formatter = dateTimeFormatters.get(tag)
  if (formatter === undefined) {
    try {
      formatter = new Intl.DateTimeFormat(tag, DATE_TIME_OPTIONS)
    } catch {
      formatter = new Intl.DateTimeFormat(LOCALE_TAGS.ru, DATE_TIME_OPTIONS)
    }
    dateTimeFormatters.set(tag, formatter)
  }
  return formatter.format(date)
}

/** Exposed so tests and snapshots can assert on the exact characters used. */
export const GLYPHS = { GROUP, MINUS } as const
